import { Box, TextField, useTheme } from "@mui/material";
import React, { FC, useCallback, useRef } from 'react';

interface EventFilterFieldProps {
    onFilterChange: (filterText: string) => void;
    filterDelay?: number;
}

/**
 * Event filter input field component with debounced search functionality
 */
const EventFilterField: FC<EventFilterFieldProps> = ({
    onFilterChange,
    filterDelay = 300
}) => {
    const theme = useTheme();
    const filterTimeoutHandle = useRef<NodeJS.Timeout | undefined>(undefined);

    const onTitleChanged = useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        // Cancel previous timeout, if any
        if (filterTimeoutHandle.current !== undefined) {
            clearTimeout(filterTimeoutHandle.current);
        }

        // Set a timeout to perform a delayed search
        filterTimeoutHandle.current = setTimeout(() => {
            onFilterChange(event.target.value);
        }, filterDelay);
    }, [onFilterChange, filterDelay]);

    return (
        <Box sx={{ mb: 2 }}>
            <TextField
                sx={{
                    m: 1,
                    width: { xs: 'calc(100% - 16px)', sm: 'min(25rem, calc(100% - 16px))' },
                    maxWidth: '25rem',
                    '& .MuiOutlinedInput-root': {
                        color: theme.palette.text.primary,
                        '& fieldset': {
                            borderColor: theme.palette.text.secondary,
                        },
                        '&:hover fieldset': {
                            borderColor: theme.palette.text.primary,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main,
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: theme.palette.text.secondary,
                        '&.Mui-focused': {
                            color: theme.palette.primary.main,
                        },
                    },
                }}
                id='event-filter'
                label={'Filter'}
                onChange={onTitleChanged}
                variant='outlined'
            />
        </Box>
    );
};

export default EventFilterField;
